import { useMutation } from '@apollo/client/react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@optools/components/ui/sheet';
import { CREATE_POLICY } from '@optools/services/operator-gateway/esni/mutations';
import { GET_VIEWING_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import { ViewingPoliciesQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { toast } from 'sonner';

import { getPolicyNameFromHref } from '../utils/policy';
import { PolicyForm, PolicyFormValues } from './policy-form';

export interface CreatePolicySheetProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function CreatePolicySheet({ onOpenChange, open }: CreatePolicySheetProps) {
  const [createPolicy, { loading }] = useMutation(CREATE_POLICY, {
    onCompleted: () => {
      toast.success('Policy successfully created');
      onOpenChange(false);
    },
    update: (cache, { data }) => {
      if (!data?.createPolicy) return;

      const query = cache.readQuery<ViewingPoliciesQuery>({
        query: GET_VIEWING_POLICIES,
      });

      if (!query) return;

      const updatedViewingPolicies = [data.createPolicy, ...query.viewingPolicies];

      cache.writeQuery<ViewingPoliciesQuery>({
        data: { viewingPolicies: updatedViewingPolicies },
        query: GET_VIEWING_POLICIES,
      });
    },
  });

  const handleSubmit = (values: PolicyFormValues) => {
    const policyName = getPolicyNameFromHref(values.href);
    createPolicy({ variables: { policy: { ...values, policyName } } });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-(--breakpoint-sm)">
        <SheetTitle className="mb-6">Viewing Policy</SheetTitle>
        <SheetDescription className="sr-only">Create a viewing policy</SheetDescription>
        <PolicyForm
          loading={loading}
          onCancel={() => onOpenChange(false)}
          onSubmit={handleSubmit}
        />
      </SheetContent>
    </Sheet>
  );
}
